import { action, computed, observable } from 'mobx'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { Id } from '@/infra/id'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { type ListInventoryItemsFn } from '@/features/inventory'
import { type InventoryItem } from '@/features/inventory/InventoryItem'
import { IncludedItemViewModel } from './IncludedItemViewModel'
import { ViewModelList } from '@/infra/view-model-list'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { catchError, EMPTY, from, switchMap } from 'rxjs'
import { shareLatest, whileActive } from '@/utils/rx-util'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { AlertRetryWrapperFactory } from '@/features/shell'

export class ManageItemsInBundleViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  readonly serviceItemVms: ViewModelList<InventoryItem, IncludedItemViewModel>

  /**
   * Search term for item selection
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * The current workspace id that the user has picked.
   */
  @observable
  accessor workspaceId = Id.none

  /**
   * The base inventory item id for the Bundle.
   */
  @observable
  accessor baseItemId = Id.none

  /**
   * The saved results from includedItemViewModels.
   */
  @observable
  accessor includedItems: IncludedItemViewModel[] = []

  /**
   * The flat-priced, active service items of the workspace.
   */
  readonly inventoryScrollProvider = new InfiniteScrollProvider<InventoryItem>({
    limit: 100,
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading service items failed',
        signal,
      )
      return withRetry(() =>
        this._searchInventoryItems(
          {
            workspaceIds: Id.some(this.workspaceId) ? [this.workspaceId] : [],
            limit: paging.limit,
            searchText: this.searchText.value,
            after: paging.after ?? undefined,
            priceTypes: ['Flat'],
            archivedStatus: 'Active',
            orderBy: 'name',
          },
          signal,
        ),
      )
    },
  })

  /**
   * Server query pipeline.
   */
  private readonly _query$ = this.searchText.debouncedValue$.pipe(
    switchMap(() =>
      from(
        this.inventoryScrollProvider.fetch({ signal: this._lifecycle.signal }),
      ).pipe(
        // The retry alert has reported the failure.
        catchError(() => EMPTY),
      ),
    ),
    shareLatest(),
  )

  constructor(
    private readonly _setIncludedItems: (
      items: IncludedItemViewModel[],
    ) => void,
    private readonly _searchInventoryItems: ListInventoryItemsFn,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this.serviceItemVms = new ViewModelList({
      models: () => this.inventoryScrollProvider.items.slice(),
      create: (model) =>
        new IncludedItemViewModel({
          isIncludedItem: this.includedItems.some(
            (includedItem) => includedItem.inventoryItemId === model.id,
          ),
          isBaseItem: model.id === this.baseItemId,
          inventoryItemId: model.id,
          inventoryItemVersionId: model.versionId,
          name: model.name,
          price: model.price,
          proPrice: model.proPrice,
          description: model.description,
          workspaceId: model.workspace?.id ?? Id.none,
          includedQuantity:
            this.includedItems.find((i) => i.inventoryItemId === model.id)
              ?.includedQuantity ?? 0,
          archived: model.archived,
          onIncludedChange: this._handleIncludedChange,
        }),
    })

    this._lifecycle.manage(this.serviceItemVms)
    this._lifecycle.manage(this._query$.pipe(whileActive(this.modal.showing$)))
  }

  @computed
  get isWorkspaceIdSet() {
    return Id.some(this.workspaceId)
  }

  @computed
  get totalSelected() {
    return this.includedItems.length
  }

  @computed
  get isBaseItemToggled() {
    return this.includedItems.some((item) => item.isBaseItem)
  }

  @computed
  get serviceItems() {
    return this.serviceItemVms.items
  }

  /**
   * Only one item can be the base item.
   */
  isBaseItemDisabled(item: IncludedItemViewModel) {
    return this.isBaseItemToggled && !item.isBaseItem
  }

  @action.bound
  setWorkspaceId(workspaceId: Id) {
    this.workspaceId = workspaceId
  }

  @action.bound
  setBaseItemId(baseItemId: Id) {
    this.baseItemId = baseItemId
  }

  @action.bound
  setCurrentlyIncludedItems(items: IncludedItemViewModel[]) {
    this.includedItems = items
  }

  @action.bound
  submit() {
    const baseItem = this.includedItems.find((item) => item.isBaseItem === true)

    this.baseItemId = baseItem?.inventoryItemId ?? Id.none

    const onlyIncludedItems = this.includedItems.filter(
      (item) => item.isIncludedItem && !item.isBaseItem,
    )

    const filteredItems = (
      baseItem ? [baseItem, ...onlyIncludedItems] : onlyIncludedItems
    ).filter((i) => i.includedQuantity > 0)

    this._setIncludedItems(filteredItems)
    this.modal.close()
  }

  @action.bound
  show() {
    this.modal.show()
  }

  @action.bound
  reset() {
    this.searchText.clear()
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  @action.bound
  private _handleIncludedChange(item: IncludedItemViewModel) {
    // Keep the included except the item being modified.
    // We do this so that we prevent duplicates in case base item is removed but the item is still included
    const includedExceptModifiedItem = this.includedItems.filter(
      (i) => i.inventoryItemId !== item.inventoryItemId,
    )
    if (item.isIncludedItem) {
      this.includedItems = [item, ...includedExceptModifiedItem]
    } else {
      this.includedItems = includedExceptModifiedItem
    }
  }
}
